import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { computeContractorSettlement } from "@torre/team";
import Swal from "sweetalert2";
import { brand } from "../../../config/theme";
import { useTheme } from "../../../context/ThemeContext";
import ApiCrudSection, {
  type Column,
  type FormField,
  type Row,
} from "../components/ApiCrudSection";
import {
  buildThemeClasses,
  formatMoney,
  STATUS_ENGAGEMENT_OPTIONS,
  engagementStatusLabel,
  STATUS_ENGAGEMENT_TONES,
} from "../theme";
import {
  fetchContractorEngagements,
  createContractorEngagement,
  updateContractorEngagement,
  deleteContractorEngagement,
  settleContractorEngagement,
  fetchContractors,
} from "../services/teamService";
import type { Contractor, ContractorEngagement } from "../types";
import { useIsStaffAdmin } from "../../../hooks/useIsStaffAdmin";

// Tareas/servicios ejecutados por contratistas (AgroCloud#60): asociación de la tarea a
// un contratista, imputación del costo al centro de costo/proyecto, estado de
// avance y liquidación/pago. El monto devengado se deriva del avance con la
// lógica compartida de `@torre/team`.
export default function EngagementsSection() {
  // #1007: solo quien administra modifica; los demás ven lo suyo en solo lectura.
  const isStaffAdmin = useIsStaffAdmin();
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);

  const [rows, setRows] = useState<Row[]>([]);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [engagements, ks] = await Promise.all([
        fetchContractorEngagements(),
        fetchContractors(),
      ]);
      setContractors(ks.items);
      // El backend ya devuelve la liquidación derivada; se recalcula en el
      // cliente para que la tabla sea consistente aunque el verbo cambie.
      setRows(
        engagements.items.map((s) => ({
          ...s,
          ...computeContractorSettlement(s),
        })) as unknown as Row[],
      );
    } catch {
      setError(t("team.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const contractorOptions = useMemo(
    () => contractors.map((k) => ({ value: k.id, label: k.name })),
    [contractors],
  );

  /** Liquida/paga al contratista el saldo devengado por el avance (AgroCloud#60). */
  const handleSettle = async (row: Row) => {
    const pending = Number(row.pendingBalance) || 0;
    if (pending <= 0) {
      await Swal.fire({
        title: t("team.settle.nothingTitle"),
        text: t("team.settle.nothingText"),
        icon: "info",
        confirmButtonColor: brand.primary,
      });
      return;
    }
    const result = await Swal.fire({
      title: t("team.settle.title"),
      text: t("team.settle.text", { value: formatMoney(pending) }),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("team.settle.confirm"),
      cancelButtonText: t("team.cancel"),
      confirmButtonColor: brand.primary,
    });
    if (!result.isConfirmed) return;
    try {
      await settleContractorEngagement(row.id);
      await load();
    } catch {
      setError(t("team.errors.settleFailed"));
    }
  };

  const columns: Column[] = [
    { key: "contractorName", label: t("team.fields.contractor") },
    { key: "description", label: t("team.fields.task") },
    {
      key: "projectId",
      label: t("team.fields.project"),
      render: (r) => String(r.projectId ?? "—"),
    },
    {
      key: "costCenterId",
      label: t("team.fields.costCenter"),
      render: (r) => String(r.costCenterId ?? "—"),
    },
    {
      key: "agreedAmount",
      label: t("team.fields.agreedAmount"),
      align: "right",
      render: (r) => formatMoney(r.agreedAmount),
    },
    {
      key: "progressPercentage",
      label: t("team.fields.progress"),
      align: "center",
      render: (r) => {
        const progress = Number(r.progressPercentage) || 0;
        return (
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-400/25">
              <div
                className="h-full rounded-full bg-primary-500"
                style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
              />
            </div>
            <span className="text-[11px]">{progress}%</span>
          </div>
        );
      },
    },
    {
      key: "earnedAmount",
      label: t("team.fields.earned"),
      align: "right",
      render: (r) => formatMoney(r.earnedAmount),
    },
    {
      key: "pendingBalance",
      label: t("team.fields.pendingBalance"),
      align: "right",
      render: (r) => (
        <span
          className={
            Number(r.pendingBalance) > 0 ? "font-semibold text-amber-500" : "text-emerald-500"
          }
        >
          {formatMoney(r.pendingBalance)}
        </span>
      ),
    },
    {
      key: "status",
      label: t("team.fields.status"),
      align: "center",
      render: (r) => (
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] ${
            STATUS_ENGAGEMENT_TONES[String(r.status)] ?? "bg-gray-400/15 text-gray-500"
          }`}
        >
          {engagementStatusLabel(t, r.status)}
        </span>
      ),
    },
  ];

  const fields: FormField[] = [
    {
      key: "contractorId",
      label: t("team.fields.contractor"),
      type: "select",
      required: true,
      options: contractorOptions,
    },
    { key: "description", label: t("team.fields.task"), required: true },
    {
      key: "agreedAmount",
      label: t("team.fields.agreedAmount"),
      type: "number",
      required: true,
    },
    { key: "startDate", label: t("team.fields.startDate"), type: "date" },
    { key: "endDate", label: t("team.fields.endDate"), type: "date" },
    {
      key: "projectId",
      label: t("team.fields.project"),
      hint: t("team.hints.project"),
    },
    {
      key: "costCenterId",
      label: t("team.fields.costCenter"),
      hint: t("team.hints.costCenter"),
    },
    {
      key: "progressPercentage",
      label: t("team.fields.progress"),
      type: "number",
      defaultValue: 0,
      hint: t("team.hints.progress"),
    },
    {
      key: "status",
      label: t("team.fields.status"),
      type: "select",
      defaultValue: "planned",
      options: STATUS_ENGAGEMENT_OPTIONS.map((e) => ({
        value: e,
        label: engagementStatusLabel(t, e),
      })),
    },
    { key: "notes", label: t("team.fields.notes"), type: "textarea" },
  ];

  const toPayload = (values: Record<string, string>): Partial<ContractorEngagement> => ({
    contractorId: values.contractorId,
    description: values.description,
    agreedAmount: values.agreedAmount ? Number(values.agreedAmount) : undefined,
    startDate: values.startDate || null,
    endDate: values.endDate || null,
    projectId: values.projectId || null,
    costCenterId: values.costCenterId || null,
    progressPercentage: values.progressPercentage ? Number(values.progressPercentage) : 0,
    status: (values.status || "planned") as ContractorEngagement["status"],
    notes: values.notes || null,
  });

  // Tarjeta de la vista grid: la tarea con su avance y su saldo por pagar.
  const gridCard = (row: Row) => (
    <div className={`rounded-xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
      <div className="mb-1 flex items-start justify-between gap-2">
        <span className={`font-semibold ${tc.text}`}>{String(row.contractorName ?? "—")}</span>
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] ${
            STATUS_ENGAGEMENT_TONES[String(row.status)] ?? "bg-gray-400/15 text-gray-500"
          }`}
        >
          {engagementStatusLabel(t, row.status)}
        </span>
      </div>
      <p className={`mb-3 text-xs ${tc.textSecondary}`}>{String(row.description ?? "")}</p>

      <div className="mb-3">
        <div className="mb-1 flex justify-between text-[11px]">
          <span className={tc.textSecondary}>{t("team.fields.progress")}</span>
          <span className={tc.text}>{Number(row.progressPercentage) || 0}%</span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-400/25">
          <div
            className="h-full rounded-full bg-primary-500"
            style={{
              width: `${Math.min(100, Math.max(0, Number(row.progressPercentage) || 0))}%`,
            }}
          />
        </div>
      </div>

      <dl className="space-y-1 text-xs">
        <div className="flex justify-between gap-2">
          <dt className={tc.textSecondary}>{t("team.fields.agreedAmount")}</dt>
          <dd className={tc.text}>{formatMoney(row.agreedAmount)}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className={tc.textSecondary}>{t("team.fields.earned")}</dt>
          <dd className={tc.text}>{formatMoney(row.earnedAmount)}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className={tc.textSecondary}>{t("team.fields.pendingBalance")}</dt>
          <dd className={Number(row.pendingBalance) > 0 ? "text-amber-500" : "text-emerald-500"}>
            {formatMoney(row.pendingBalance)}
          </dd>
        </div>
      </dl>
    </div>
  );

  return (
    <ApiCrudSection
      readOnly={!isStaffAdmin}
      title={t("team.menu.engagements")}
      subtitle={t("team.engagementsSubtitle")}
      icon="ri-hammer-line"
      columns={columns}
      fields={fields}
      rows={rows}
      loading={loading}
      error={error}
      searchKey="contractorName"
      addLabel={t("team.newEngagement")}
      gridCard={gridCard}
      extraActions={isStaffAdmin ? (row) => (
        <button
          title={t("team.settle.action")}
          aria-label={`${t("team.settle.action")} ${String(row.description ?? "")}`}
          onClick={() => void handleSettle(row)}
          className="rounded-md p-1.5 text-emerald-500 hover:bg-emerald-500/10"
        >
          <i className="ri-hand-coin-line" />
        </button>
      ) : undefined}
      onCreate={async (values) => {
        await createContractorEngagement(toPayload(values));
        await load();
      }}
      onUpdate={async (id, values) => {
        await updateContractorEngagement(id, toPayload(values));
        await load();
      }}
      onDelete={async (id) => {
        await deleteContractorEngagement(id);
        await load();
      }}
    />
  );
}
